import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Network, Menu, X } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#hero', label: 'Accueil' },
    { href: '#portfolio', label: 'Portfolio' },
    { href: '#expertise', label: 'Expertise' },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/5 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <motion.a
            href="#hero"
            data-testid="logo"
            className="flex items-center gap-2 sm:gap-3 group"
            whileHover={{ scale: 1.02 }}
          >
            <Network className="w-8 h-8 sm:w-10 sm:h-10 text-[#4facfe] group-hover:text-[#00f2fe] transition-colors" />
            <span className="font-outfit text-xl sm:text-2xl font-bold text-white tracking-tight">
              TECHNEXUS
            </span>
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <motion.a
                key={link.href}
                href={link.href}
                className="text-white/70 hover:text-white font-inter text-sm font-medium transition-colors relative group"
                whileHover={{ y: -2 }}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#00f2fe] to-[#4facfe] group-hover:w-full transition-all duration-300" />
              </motion.a>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <motion.button
            data-testid="cta-header-button"
            className="hidden md:flex bg-gradient-to-r from-[#00f2fe] to-[#4facfe] text-white font-semibold rounded-lg px-6 py-2.5 text-sm hover:opacity-90 transition-opacity shadow-[0_0_15px_rgba(79,172,254,0.3)]"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Commencer
          </motion.button>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="md:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          initial={false}
          animate={{
            height: isMobileMenuOpen ? 'auto' : 0,
            opacity: isMobileMenuOpen ? 1 : 0,
          }}
          className="md:hidden overflow-hidden"
        >
          <nav className="py-4 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block text-white/70 hover:text-white font-inter text-base font-medium transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <button
              data-testid="cta-mobile-button"
              className="w-full bg-gradient-to-r from-[#00f2fe] to-[#4facfe] text-white font-semibold rounded-lg px-6 py-3 text-sm hover:opacity-90 transition-opacity mt-4"
            >
              Commencer
            </button>
          </nav>
        </motion.div>
      </div>
    </motion.header>
  );
};

export default Header;
